'use client';

/* eslint-disable @next/next/no-img-element */
import cn from '@sindresorhus/class-names';
import {useCallback, useState} from 'react';

import {TSSPParsed} from '@web-catalog/components/Ads/SSP/types';
import {
  copyToClipboard,
  mapCreativeAssets,
} from '@web-catalog/components/Ads/SSP/utils';
import {useAdAnalytics} from '@web-catalog/components/Ads/useAdAnalytics';

import {Icon} from '@web-catalog/ui-kit/Icon';
import {
  CancelOutlineSvg,
  ChainSvg,
  CopyOutlineSvg,
  HideOutlineSvg,
  MoreHorizontalSvg,
  RectangleInfoOutlineSvg,
  SpinnerSvg,
} from '@web-catalog/ui-kit/SvgIcons';
import {Text} from '@web-catalog/ui-kit/Text';

import styles from './shared.module.scss';

type TSSPBanner = {
  adInfo: TSSPParsed;
  isAdLoading: boolean;
  onClose: () => void;
  onOpen: () => void;
};

export const SSPBanner = ({
  adInfo,
  isAdLoading,
  onClose,
  onOpen,
}: TSSPBanner) => {
  const [overlayActive, setOverlayActive] = useState(false);
  const adLog = useAdAnalytics({platform: 'SSP', adType: 'sticky_banner'});

  const handleOpenOverlay = useCallback(() => {
    adLog.clickedOptions(adInfo?.slotid);
    setOverlayActive(true);
  }, [adInfo?.slotid, adLog]);

  const handleCloseOverlay = useCallback(() => {
    setOverlayActive(false);
  }, []);

  const erId = adInfo?.creative.native.ext.adChoices?.options.find((option) =>
    option.name.includes('Скопировать ERID'),
  );

  const adLinkToCopy = adInfo?.creative.native.ext.adChoices?.options.find(
    (option) => option.name.includes('Скопировать ссылку'),
  );

  const aboutAdLink = adInfo?.creative.native.ext.adChoices?.options.find(
    (adOption) => adOption.name === 'О рекламодателе',
  );

  const handleCopyErId = useCallback(() => {
    if (erId?.copyText) {
      copyToClipboard(erId?.copyText);
    }
  }, [erId?.copyText]);

  const handleCopyAdLink = useCallback(() => {
    if (adLinkToCopy?.copyText) {
      copyToClipboard(adLinkToCopy.copyText);
    }
  }, [adLinkToCopy?.copyText]);

  const handleAboutAd = useCallback(() => {
    if (aboutAdLink?.clickLink) {
      window.open(aboutAdLink?.clickLink, '_blank');
    }
  }, [aboutAdLink?.clickLink]);

  const mappedAssets = mapCreativeAssets(adInfo.creative.native.assets);

  return (
    <div className={styles.wrapper}>
      <div className={styles.banner}>
        <div
          className={styles.content}
          onClick={onOpen}
          data-testid="sspContent"
        >
          {mappedAssets.image.img?.url && (
            <img src={mappedAssets.image.img?.url || ''} alt="" />
          )}
          <div className={styles.info}>
            <div className={styles.title}>
              <Text typography="content-m">
                {mappedAssets.title?.title?.text}
              </Text>
            </div>
            <div>
              <Text typography="caption">
                {mappedAssets.subtitle?.data?.value}
              </Text>
            </div>
            <div className={styles.adCaption}>
              {mappedAssets.caption?.data?.value} •{' '}
              {adInfo.creative.native.ext.agerestrictions}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <Icon
            svg={MoreHorizontalSvg}
            className={styles.optionsIcon}
            onClick={handleOpenOverlay}
          />
          {isAdLoading ? (
            <Icon className={styles.spinner} svg={SpinnerSvg} />
          ) : (
            <Icon
              className={styles.closeIcon}
              svg={CancelOutlineSvg}
              onClick={onClose}
              data-testid="sspCloseButton"
            />
          )}
        </div>

        <div
          className={cn(styles.overlay, {
            [styles.overlayActive]: overlayActive,
          })}
        >
          <div className={styles.overlayClose}>
            <Icon
              className={styles.closeIcon}
              svg={CancelOutlineSvg}
              onClick={handleCloseOverlay}
            />
          </div>
          <div>
            <div className={styles.actionItem} onClick={onClose}>
              <div className={styles.actionIcon}>
                <Icon className={styles.closeIcon} svg={HideOutlineSvg} />
              </div>
              <div>Скрыть объявление</div>
            </div>
            {aboutAdLink?.clickLink && (
              <div className={styles.actionItem} onClick={handleAboutAd}>
                <div className={styles.actionIcon}>
                  <Icon
                    className={styles.closeIcon}
                    svg={RectangleInfoOutlineSvg}
                  />
                </div>
                <div>О рекламодателе</div>
              </div>
            )}
            {adLinkToCopy?.copyText && (
              <div className={styles.actionItem} onClick={handleCopyAdLink}>
                <div className={styles.actionIcon}>
                  <Icon className={styles.closeIcon} svg={ChainSvg} />
                </div>
                <div>Скопировать ссылку</div>
              </div>
            )}
            {erId?.copyText && (
              <div className={styles.actionItem} onClick={handleCopyErId}>
                <div className={styles.actionIcon}>
                  <Icon className={styles.closeIcon} svg={CopyOutlineSvg} />
                </div>
                <div>
                  <div>Скопировать ERID</div>
                  <div className={styles.subtitle}>{erId?.copyText}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
